import React, { useState, useEffect } from 'react';
import {
  FiKey as Key, FiPlus as Plus, FiCopy as Copy, FiTrash2 as Trash2, FiCheck as Check,
  FiAlertTriangle as AlertTriangle, FiRefreshCw as RefreshCw, FiShield as Shield,
  FiFileText as FileText, FiActivity as Activity,
  FiSquare as Square, FiCheckSquare as CheckSquare, FiChevronRight as ChevronRight,
  FiSearch as Search
} from 'react-icons/fi';
import { createAPIKey, listAPIKeys, revokeAPIKey } from '../../../services/apiKeysApi';
import { bulkRevokeApiKeys, bulkAssignApiKeyProject, bulkAssignApiKeyWorkload } from '../../../services/aiOperationsApi';
import ScopeSelector from '../../ai-operations/components/ScopeSelector';
import ProjectAssignmentSelect from '../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../components/WorkloadAssignmentSelect';
import { useWorkloads } from '../../../features/hub/workloads/hooks/useWorkloads';
import { useProject } from '../../../contexts/ProjectContext';

interface APIKey {
  id: string;
  name: string;
  description?: string;
  key_prefix?: string;
  scopes?: string;
  usage_count?: number;
  is_active?: boolean;
  expires_at?: string | null;
  created_at?: string;
  project_id?: string | null;
  workload_id?: string | null;
  last_used_at?: string | null;
}

// Helper function for relative time formatting
const formatRelativeDate = (timestamp: string | null | undefined): string => {
  if (!timestamp) return 'Never';

  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString();
};

const APIKeysContent: React.FC = () => {
  const { projects } = useProject();
  const { workloads } = useWorkloads();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>(['read:prompts', 'read:datasets', 'read:configs']);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [keyConfirmed, setKeyConfirmed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailKey, setDetailKey] = useState<APIKey | null>(null);

  useEffect(() => {
    loadAPIKeys();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const loadAPIKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAPIKeys(includeInactive);
      setApiKeys(data || []);
    } catch (err: unknown) {
      console.error('Error loading API keys:', err);
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim() || newKeyScopes.length === 0) return;

    setCreating(true);
    try {
      const result = await createAPIKey({
        name: newKeyName,
        description: newKeyDescription || null,
        scopes: Array.isArray(newKeyScopes) ? newKeyScopes.join(',') : newKeyScopes,
        expires_at: null
      });
      setNewKey(result.key || result.api_key);
      await loadAPIKeys();
    } catch (err: unknown) {
      console.error('Error creating API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId: string, keyName: string) => {
    if (!globalThis.confirm(`Are you sure you want to revoke "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await revokeAPIKey(keyId);
      await loadAPIKeys();
    } catch (err: unknown) {
      console.error('Error revoking API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString();
  };

  const isExpired = (expiresAt: string | null | undefined): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getKeyStatus = (key: APIKey): string => {
    if (key.is_active === false) return 'revoked';
    if (isExpired(key.expires_at)) return 'expired';
    return 'active';
  };

  const filteredKeys = apiKeys.filter((key) => {
    const status = getKeyStatus(key);
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        key.name.toLowerCase().includes(q) ||
        (key.key_prefix?.toLowerCase().includes(q)) ||
        (key.description?.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const activeIds = filteredKeys.filter((k) => getKeyStatus(k) === 'active').map((k) => k.id);
    if (activeIds.length > 0 && activeIds.every((id) => selectedIds.has(id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        activeIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        activeIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const getProjectName = (projectId: string | null | undefined): string | null => {
    if (!projectId) return null;
    return projects.find((project) => project.id === projectId)?.name ?? null;
  };

  const getWorkloadName = (workloadId: string | null | undefined): string | null => {
    if (!workloadId) return null;
    return workloads.find((workload) => workload.id === workloadId)?.name ?? null;
  };

  const handleBulkRevoke = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!globalThis.confirm(`Are you sure you want to revoke ${count} API key${count > 1 ? 's' : ''}? This action cannot be undone.`)) {
      return;
    }
    try {
      await bulkRevokeApiKeys(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadAPIKeys();
    } catch (err: unknown) {
      console.error('Error bulk revoking API keys:', err);
      setError(err instanceof Error ? err.message : 'Failed to revoke API keys');
    }
  };

  const openDetailModal = (key: APIKey) => {
    setDetailKey(key);
  };

  const activeFilteredIds = filteredKeys.filter((k) => getKeyStatus(k) === 'active').map((k) => k.id);
  const allActiveSelected = activeFilteredIds.length > 0 && activeFilteredIds.every((id) => selectedIds.has(id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={toggleSelectAll}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            title={allActiveSelected ? 'Deselect all' : 'Select all active'}
          >
            {allActiveSelected && activeFilteredIds.length > 0 ? (
              <CheckSquare className="w-5 h-5 text-blue-600" />
            ) : (
              <Square className="w-5 h-5 text-gray-400" />
            )}
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search keys..."
              className="pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-56"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              if (e.target.value !== 'all') setIncludeInactive(true);
            }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
            <option value="expired">Expired</option>
          </select>
          <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />{' '}
            Show revoked
          </span>
          <button
            onClick={loadAPIKeys}
            className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <button
          onClick={() => {
            setShowCreateModal(true);
            setNewKeyName('');
            setNewKeyDescription('');
            setNewKeyScopes(['read:prompts', 'read:datasets', 'read:configs']);
            setNewKey(null);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Create API Key
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {selectedIds.size} key{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              onAssign={async (projectId) => {
                await bulkAssignApiKeyProject(Array.from(selectedIds), projectId);
                setSelectedIds(new Set());
                await loadAPIKeys();
              }}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={async (workloadId) => {
                await bulkAssignApiKeyWorkload(Array.from(selectedIds), workloadId);
                setSelectedIds(new Set());
                await loadAPIKeys();
              }}
              size="sm"
            />
            <button
              onClick={handleBulkRevoke}
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Revoke Selected
            </button>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {apiKeys.length === 0 && (
          <div className="p-12">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Create Your First API Key
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                API keys authenticate your agents with the Aivilo SDK.
                They allow secure access to prompts, datasets, and configurations.
              </p>

              {/* What API keys enable */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">With an API key, your agents can:</h4>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <li className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Fetch <strong>prompts</strong> from production environments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Activity className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Send <strong>telemetry</strong> data for monitoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span>Access resources with <strong>scoped permissions</strong></span>
                  </li>
                </ul>
              </div>

              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium"
              >
                <Plus className="w-5 h-5" />
                Create First API Key
              </button>
            </div>
          </div>
        )}
        {apiKeys.length > 0 && filteredKeys.length === 0 && (
          <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
            No API keys match your filters.
          </div>
        )}
        {apiKeys.length > 0 && filteredKeys.length > 0 && (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredKeys.map((key) => {
              const status = getKeyStatus(key);
              return (
                <div
                  key={key.id}
                  onClick={() => openDetailModal(key)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openDetailModal(key); }} role="button" tabIndex={0}
                  className={`px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${status === 'active' ? '' : 'opacity-60'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
                        <button onClick={() => toggleSelect(key.id)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                          {selectedIds.has(key.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
                        </button>
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900 dark:text-white">{key.name}</span>
                          {status === 'revoked' && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 rounded">
                              Revoked
                            </span>
                          )}
                          {status === 'expired' && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 rounded">
                              Expired
                            </span>
                          )}
                          {status !== 'revoked' && status !== 'expired' && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                              Active
                            </span>
                          )}
                          <span className="text-xs text-gray-500 font-mono">{key.key_prefix}...</span>
                          {getProjectName(key.project_id) && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                              {getProjectName(key.project_id)}
                            </span>
                          )}
                          {getWorkloadName(key.workload_id) && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                              {getWorkloadName(key.workload_id)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span>Created {formatTimestamp(key.created_at)}</span>
                          {key.last_used_at && <span>Last used {formatRelativeDate(key.last_used_at)}</span>}
                          {key.scopes && <span>{key.scopes}</span>}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full mx-4 p-6 max-w-2xl">
            {newKey ? (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    API Key Created Successfully
                  </h3>
                </div>

                {/* Warning Banner */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-6">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                        Save this API key now!
                      </h4>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        You won't be able to see it again. Copy it to a secure location before closing this dialog.
                      </p>
                    </div>
                  </div>
                </div>

                {/* API Key Display */}
                <div className="mb-6">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your API Key
                  </span>
                  <div className="relative">
                    <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 pr-12 border border-gray-700">
                      <code className="text-green-400 font-mono text-sm break-all select-all">
                        {newKey}
                      </code>
                    </div>
                    <button
                      onClick={() => copyToClipboard(newKey, 'new-key')}
                      className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedId === 'new-key' ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-300" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Key Details */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Name:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{newKeyName}</span>
                  </div>
                  {newKeyDescription && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Description:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{newKeyDescription}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Scopes:</span>
                    <div className="flex flex-wrap gap-1 justify-end max-w-xs">
                      {newKeyScopes.map((scope: string) => (
                        <span key={scope} className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Confirmation Checkbox */}
                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={keyConfirmed}
                      onChange={(e) => setKeyConfirmed(e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      I have saved this API key in a secure location and understand that I won't be able to see it again.
                    </span>
                  </label>
                </div>

                {/* Action Button */}
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewKey(null);
                      setKeyConfirmed(false);
                    }}
                    disabled={!keyConfirmed}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      keyConfirmed
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Done
                  </button>
                </div>

                {/* Tip */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <strong>Tip:</strong> Store your API key in an environment variable or secret management system.
                    Never commit it to version control.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  Generate New API Key
                </h3>
                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input id="name"
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., Production API Key"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description (optional)
                    </label>
                    <input id="description"
                      type="text"
                      value={newKeyDescription}
                      onChange={(e) => setNewKeyDescription(e.target.value)}
                      placeholder="What will this key be used for?"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Scopes */}
                  <ScopeSelector
                    selectedScopes={newKeyScopes}
                    onChange={setNewKeyScopes}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateKey}
                    disabled={!newKeyName.trim() || newKeyScopes.length === 0 || creating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {creating ? 'Creating...' : 'Generate Key'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">API Key Details</h3>
              <button
                onClick={() => setDetailKey(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Name</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{detailKey.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
                <span>
                  {getKeyStatus(detailKey) === 'revoked' && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 rounded">Revoked</span>
                  )}
                  {getKeyStatus(detailKey) === 'expired' && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 rounded">Expired</span>
                  )}
                  {getKeyStatus(detailKey) !== 'revoked' && getKeyStatus(detailKey) !== 'expired' && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">Active</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Key Prefix</span>
                <code className="text-sm text-gray-600 dark:text-gray-400 font-mono">{detailKey.key_prefix}...</code>
              </div>
              {detailKey.description && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Description</span>
                  <span className="text-sm text-gray-900 dark:text-white text-right max-w-xs">{detailKey.description}</span>
                </div>
              )}
              {detailKey.scopes && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Scopes</span>
                  <div className="flex flex-wrap gap-1 justify-end max-w-xs">
                    {detailKey.scopes.split(',').map((scope) => (
                      <span key={scope} className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        {scope.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Created</span>
                <span className="text-sm text-gray-900 dark:text-white">{formatTimestamp(detailKey.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Last Used</span>
                <span className="text-sm text-gray-900 dark:text-white">{formatRelativeDate(detailKey.last_used_at)}</span>
              </div>
              {detailKey.usage_count != null && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Usage Count</span>
                  <span className="text-sm text-gray-900 dark:text-white">{detailKey.usage_count.toLocaleString()}</span>
                </div>
              )}
              {detailKey.expires_at && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Expires</span>
                  <span className="text-sm text-gray-900 dark:text-white">{formatTimestamp(detailKey.expires_at)}</span>
                </div>
              )}
            </div>

            <div className="flex justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              {getKeyStatus(detailKey) === 'active' && (
                <button
                  onClick={() => {
                    handleDeleteKey(detailKey.id, detailKey.name);
                    setDetailKey(null);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Revoke Key
                </button>
              )}
              <button
                onClick={() => setDetailKey(null)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm ml-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeysContent;
